import {
  WSActions,
  WSAlert,
  WSAlertProps,
  WSCard,
  WSControlGroupCardOption,
  WSControlGroupCards,
  WSInfoBox,
  WSInfoBoxProps,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import {
  PropsWithChildren,
  ReactNode,
  useEffect,
  useMemo,
  useState
} from "react";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";

export type StepLayoutWithOptionsProps<Value> = {
  title?: string;
  description?: string;
  options: WSControlGroupCardOption[];
  onBack?: () => void;
  onNext?: (value: Value) => void;
  error?: any;
  isLoading?: boolean;
  alerts?: (WSAlertProps & { children?: ReactNode })[];
  infoBox?:
    | PropsWithChildren<WSInfoBoxProps>
    | ((value: Value) => PropsWithChildren<WSInfoBoxProps> | undefined);
};

export function StepLayoutWithOptions<Value extends string = string>({
  title,
  description,
  onBack,
  onNext,
  options,
  error,
  isLoading,
  alerts = [],
  infoBox
}: StepLayoutWithOptionsProps<Value>) {
  const [value, setValue] = useState<Value>(options[0]?.value as Value);

  useEffect(() => {
    if (!value && options.length > 0) {
      setValue(options[0]?.value as Value);
    }
  }, [options, value]);

  const infoBoxProps = useMemo(() => {
    if (typeof infoBox === "function") {
      return infoBox(value);
    }

    return infoBox;
  }, [infoBox, value]);

  return (
    <WSList gap="2XL">
      {alerts.map((alert, index) => (
        <WSAlert key={String(index) + alert.title} {...alert} />
      ))}

      <WSCard
        header={{
          label: {
            text: title
          }
        }}
      >
        <WSList gap="2XL">
          {description && (
            <WSText.ParagraphSm color="gray500">
              {description}
            </WSText.ParagraphSm>
          )}

          <WSControlGroupCards
            options={options}
            value={value}
            onChange={newValue => {
              setValue(newValue as Value);
            }}
          />

          {infoBoxProps && <WSInfoBox {...infoBoxProps} />}

          <WSErrorMessage contextKey="Other" error={error} />
        </WSList>
      </WSCard>

      <WSActions
        alignment="fill"
        buttons={[
          {
            label: "Back",
            kind: "Tertiary",
            onClick: onBack,
            visible: !!onBack
          },
          {
            label: "Continue",
            onClick: () => {
              onNext?.(value);
            },
            loading: isLoading
          }
        ]}
      />
    </WSList>
  );
}
